import { ObservableMap, runInAction } from 'mobx';
import identity from 'lodash/identity';
import { TrackModel, TrackProps } from './track';
import { SpeakerModel } from './speaker';
import { AsyncResult } from '../core';
import { ApiVariables, ApiQueryOptions } from '../api/apiSchema';
import { Store } from '../store/store';
import { SyncStatus, ValidatorResponse } from './job';
import { MomentModel } from './moment';

// ---------------------------------------------------------------------------
// Track sorting
// ---------------------------------------------------------------------------

export const trackSorter = (a: TrackModel, b: TrackModel) => {
  return (
    ((+b.isTopic) - (+a.isTopic)) ||
    ((+b.isChapter) - (+a.isChapter)) ||
    ((+b.isSubtopic) - (+a.isSubtopic)) ||
    ((+b.isGeneric) - (+a.isGeneric)) ||
    ((+b.isParagraph) - (+a.isParagraph)) ||
    ((+b.isTranscript) - (+a.isTranscript)) ||
    a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// Track identity key — determines which property uniquely identifies a track
// within its type group.
// ---------------------------------------------------------------------------

export const trackIdentityKey = (type: string): 'type' | 'momentType' | 'speakerId' => {
  if (type === 'Moment')
    return 'momentType';
  else if (type === 'Transcript')
    return 'speakerId';

  return 'type';
}

// ---------------------------------------------------------------------------
// Track computed helpers — pure functions on track lookups / lists
// ---------------------------------------------------------------------------

export function sortedTracks(trackLookup: ObservableMap<string, TrackModel>): TrackModel[] {
  return [...trackLookup.values()].sort(trackSorter);
}

export function filterActiveTracks(tracks: TrackModel[]): TrackModel[] {
  return tracks.filter(track => track.isActive);
}

export function filterTranscriptTracks(tracks: TrackModel[]): TrackModel[] {
  return tracks.filter(track => track.isTranscript);
}

export function filterMomentTracks(tracks: TrackModel[]): TrackModel[] {
  return tracks.filter(track => track.isGeneric);
}

export function filterTopicTracks(tracks: TrackModel[]): TrackModel[] {
  return tracks.filter(track => track.isTopic);
}

export function filterSubtopicTracks(tracks: TrackModel[]): TrackModel[] {
  return tracks.filter(track => track.isSubtopic);
}

export function filterActiveTopicTracks(topicTracks: TrackModel[]): TrackModel[] {
  return topicTracks.filter(track => track.isActive);
}

export function findActiveTopicTrack(topicTracks: TrackModel[]): TrackModel | null {
  return topicTracks.find(track => track.isActive) ?? null;
}

export function filterActiveSubtopicTracks(subtopicTracks: TrackModel[]): TrackModel[] {
  return subtopicTracks.filter(track => track.isActive);
}

export function findActiveSubtopicTrack(subtopicTracks: TrackModel[]): TrackModel | null {
  return subtopicTracks.find(track => track.isActive) ?? null;
}

// ---------------------------------------------------------------------------
// Track access helpers
// ---------------------------------------------------------------------------

export function hasTrackInLookup(trackLookup: ObservableMap<string, TrackModel>, id: string): boolean {
  return trackLookup.has(id);
}

export function getTrackFromLookup(trackLookup: ObservableMap<string, TrackModel>, id: string): TrackModel | null {
  return trackLookup.get(id) || null;
}

export function findTrackByName(tracks: TrackModel[], name: string): TrackModel | null {
  if (!name)
    return null;
  return tracks.find(t => t.name === name) || null;
}

export function findTranscriptTrackBySpeaker(transcriptTracks: TrackModel[], speakerId: string | null): TrackModel | null {
  if (!speakerId)
    return null;
  return transcriptTracks.find(t => t.speakerId === speakerId) || null;
}

export function findMomentTrackByMomentType(momentTracks: TrackModel[], momentType: string | null): TrackModel | null {
  if (!momentType)
    return null;
  return momentTracks.find(t => t.momentType === momentType) || null;
}

export function insertTrackIntoLookup(
  props: TrackProps,
  trackLookup: ObservableMap<string, TrackModel>,
  store: Store,
): TrackModel {
  const track = new TrackModel(props, store);
  trackLookup.set(track.id, track);
  return track;
}

// ---------------------------------------------------------------------------
// Track validation helpers
// ---------------------------------------------------------------------------

export function findMultipleActiveTrackTypes(activeTracks: TrackModel[]): string[] {
  return activeTracks.reduce((acc, arg) => {
    const identityProperty: keyof TrackModel = trackIdentityKey(arg.type);
    const identityValue = arg[identityProperty];
    const isMultipleTimes = activeTracks.filter(track => track[identityProperty] === arg[identityProperty]).length > 1;

    if (identityValue && isMultipleTimes && !acc.includes(identityValue))
      acc.push(identityValue);

    return acc;
  }, [] as string[]);
}

export function validateSetToDone(
  multipleActiveTrackTypes: string[],
  uncontainedActiveSubtopics: MomentModel[],
): ValidatorResponse {
  const isError = multipleActiveTrackTypes.length > 0 || uncontainedActiveSubtopics.length > 0;
  return {
    multipleActiveTrackTypes,
    moments: uncontainedActiveSubtopics,
    error: isError
  }
}

export function findActiveDuplicateByTrack(
  activeTracks: TrackModel[],
  track: Pick<TrackModel, 'type' | 'momentType' | 'speakerId'> & { id?: string },
): TrackModel[] {
  const identityKey = trackIdentityKey(track.type);
  if (track.id) {
    return activeTracks.filter(item =>
      track.type === item.type && track[identityKey] === item[identityKey] && track.id !== item.id);
  }

  return activeTracks.filter(item =>
    track.type === item.type && track[identityKey] === item[identityKey]);
}

export function checkIfActiveDuplicateForTrack(
  activeTracks: TrackModel[],
  track: Pick<TrackModel, 'type' | 'momentType' | 'speakerId'> & { id?: string },
): boolean {
  return findActiveDuplicateByTrack(activeTracks, track).length > 0;
}

// ---------------------------------------------------------------------------
// Speaker computed helpers
// ---------------------------------------------------------------------------

export function collectSpeakerIds(tracks: TrackModel[], moments: MomentModel[]): Set<string> {
  return new Set([
    ...tracks.map(track => track.speakerId),
    ...moments.map(moment => moment.speakerId)
  ].filter(identity));
}

export function filterSpeakersByIds(
  jobId: string,
  speakerIds: Set<string>,
  store: Store,
): SpeakerModel[] {
  return store.speakerManager
    .getJobSpeakers(jobId)
    .filter(speaker => speakerIds.has(speaker.id));
}

export function filterPrivateSpeakersByIds(
  jobId: string,
  speakerIds: Set<string>,
  store: Store,
): SpeakerModel[] {
  return store.speakerManager
    .getJobPrivateSpeakers(jobId)
    .filter(speaker => speakerIds.has(speaker.id));
}

export function collectTrackSpeakerIds(tracks: TrackModel[]): Set<string> {
  return new Set(tracks.map(track => track.speakerId).filter(identity));
}

export function resolveTrackSpeakers(
  trackSpeakerIds: Set<string>,
  store: Store,
): SpeakerModel[] {
  return Array.from(trackSpeakerIds)
    .map(trackId => store.speakerManager.maybeGetSpeaker(trackId))
    .filter(identity) as SpeakerModel[];
}

// ---------------------------------------------------------------------------
// Interface describing the subset of JobModel needed by the track/speaker
// API helpers, avoiding a direct import of JobModel and preventing circular
// imports.
// ---------------------------------------------------------------------------

export interface TrackApiContext {
  readonly id: string;
  readonly store: Store;
  trackLookup: ObservableMap<string, TrackModel>;
  speakerIds: Set<string>;
  speakersSyncStatus: SyncStatus;

  getTrack(id: string): TrackModel | null;
  apiFetchTracks(opts?: ApiQueryOptions): AsyncResult<boolean>;
  apiFetchMoments(more?: boolean, opts?: ApiQueryOptions): AsyncResult<boolean>;
}

// ---------------------------------------------------------------------------
// Track API methods — standalone async functions that take a context
// ---------------------------------------------------------------------------

export async function apiFetchTracks(
  ctx: TrackApiContext,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const api = ctx.store.api;
  const [tracksRes, err] = await api.getTracks({ jobId: ctx.id }, opts);
  if (err)
    return [null, err];

  const items = tracksRes?.getTracks || [];

  runInAction(() => {
    ctx.trackLookup.clear();
    items.forEach(track => {
      ctx.trackLookup.set(track.id, new TrackModel(track, ctx.store));
    });
  });
  return [true];
}

export async function apiAddTrack(
  ctx: TrackApiContext,
  args: ApiVariables<'addTrack'>,
): AsyncResult<TrackModel> {
  const api = ctx.store.api;
  const [res, err] = await api.addTrack(args);
  if (err)
    return [null, err];

  const trackId = res?.addTrack.id;

  if (!trackId)
    return [null, new Error('Added track ID was not returned')];

  const [, refErr] = await ctx.apiFetchTracks();
  if (refErr)
    return [null, refErr];

  const track = trackId && ctx.getTrack(trackId);
  if (track)
    return [track];

  return [null, new Error('Added track was not fetched')];
}

export async function apiUpdateTrack(
  ctx: TrackApiContext,
  args: ApiVariables<'updateTrack'>,
  deps: ('tracks' | 'moments')[] = ['tracks'],
): AsyncResult<TrackModel> {
  const api = ctx.store.api;
  const [res, err] = await api.updateTrack(args);
  if (err)
    return [null, err];

  const trackId = res?.updateTrack.id;

  if (!trackId)
    return [null, new Error('Updated track ID was not returned')];

  let requests: AsyncResult<boolean>[] = [];
  deps.forEach(item => {
    switch (item) {
      case 'tracks':
        requests = [
          ...requests,
          ctx.apiFetchTracks()
        ]
        break;
      case 'moments':
        requests = [
          ...requests,
          ctx.apiFetchMoments()
        ]
        break;
    }
  })

  const resAll = await Promise.all(requests);
  const err1 = resAll.find(res => res && !!res[1])?.[1];

  if (err1)
    return [null, err1]

  const track = trackId && ctx.getTrack(trackId);
  if (track)
    return [track];

  return [null, new Error('Updated track was not fetched')];
}

export async function apiMergeTracks(
  ctx: TrackApiContext,
  args: ApiVariables<'mergeTracks'>,
): AsyncResult<Boolean> {
  const api = ctx.store.api;
  const [res, err] = await api.mergeTracks(args);
  if (err)
    return [null, err];

  const [
    [, err1],
    [, err2]
  ] = await Promise.all([
    ctx.apiFetchTracks(),
    ctx.apiFetchMoments()
  ]);

  if (err1 || err2)
    return [null, err1 || err2];

  if (res?.mergeTracks)
    return [res?.mergeTracks];

  return [null, new Error('No response returned for the merge request')];
}

export async function apiDuplicateTrack(
  ctx: TrackApiContext,
  args: ApiVariables<'duplicateTrack'>,
): AsyncResult<TrackModel> {
  const api = ctx.store.api;
  const [res, err] = await api.duplicateTrack(args);
  if (err)
    return [null, err];

  const trackId = res?.duplicateTrack.id;

  const [
    [, err1],
    [, err2]
  ] = await Promise.all([
    ctx.apiFetchTracks(),
    ctx.apiFetchMoments()
  ]);

  if (err1 || err2)
    return [null, err1 || err2];

  const track = trackId && ctx.getTrack(trackId);
  if (track)
    return [track];

  return [null, new Error('No response returned for the duplicate request')];
}

// ---------------------------------------------------------------------------
// Speaker API methods — standalone async functions
// ---------------------------------------------------------------------------

export async function apiFetchSpeakers(
  ctx: TrackApiContext,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const [, err] = await ctx.store.speakerManager.apiFetchSpeakers({ jobId: ctx.id }, opts);
  if (err)
    return [null, err];

  return [true];
}

export async function apiBatchFetchSpeakers(
  ctx: TrackApiContext,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  ctx.speakersSyncStatus = SyncStatus.Fetching as SyncStatus;

  const ids = Array.from(ctx.speakerIds);
  if (ids.length < 1)
    return [true];

  const [, err] = await ctx.store.speakerManager.apiBatchFetchSpeakers({ ids }, opts);
  if (err) {
    ctx.speakersSyncStatus = SyncStatus.Error as SyncStatus;
    return [null, err];
  }

  ctx.speakersSyncStatus = SyncStatus.Fetched as SyncStatus;
  return [true];
}

export async function addSpeaker(
  store: Store,
  props: ApiVariables<'addSpeaker'>,
): AsyncResult<boolean> {
  const [, err] = await store.apiAddSpeaker(props);
  if (err)
    return [null, err];

  return [true];
}

export async function apiUpdateSpeaker(
  store: Store,
  props: ApiVariables<'updateSpeaker'>,
): AsyncResult<boolean> {
  const [, err] = await store.apiUpdateSpeaker(props);
  if (err)
    return [null, err];

  return [true];
}
